import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Instagram, Mail, ArrowLeft, Send, User, MessageSquare, Play, Star, Share2, Download, Facebook, Twitter, Linkedin, Link as LinkIcon, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BookingModal from '../components/BookingModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, onRate, interactive = false, size = 20 }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : undefined}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={interactive ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            size={size}
            className={`${
              star <= (hoverRating || rating)
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-600'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const ModelProfile = () => {
  const { modelId } = useParams();
  const { toast } = useToast();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0 });
  const [enquiryData, setEnquiryData] = useState({
    enquirer_name: '',
    enquirer_email: '',
    message: ''
  });
  const [reviewData, setReviewData] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 5,
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchModel();
    fetchReviews();
    
    // Check if model was just approved via email
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('approved') === 'true') {
      toast({
        title: "Model Approved!",
        description: "This model's profile is now live on the website.",
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [modelId]);

  const fetchModel = async () => {
    try {
      const response = await axios.get(`${API}/models/${modelId}`);
      setModel(response.data);
    } catch (error) {
      console.error('Error fetching model:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/models/${modelId}/reviews`);
      setReviews(response.data.reviews || []);
      setReviewStats({
        total: response.data.total_reviews || 0,
        average: response.data.average_rating || 0
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleEnquiryChange = (e) => {
    const { name, value } = e.target;
    setEnquiryData(prev => ({ ...prev, [name]: value }));
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({ ...prev, [name]: value }));
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/models/${modelId}/reviews`, reviewData);

      if (response.data.status === 'success') {
        toast({
          title: "Review Submitted!",
          description: "Thank you for your feedback.",
        });
        setShowReviewForm(false);
        setReviewData({ reviewer_name: '', reviewer_email: '', rating: 5, comment: '' });
        fetchReviews(); // Refresh reviews
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/models/${modelId}/enquiry`, {
        model_id: modelId,
        ...enquiryData
      });

      if (response.data.status === 'success') {
        toast({
          title: "Enquiry Sent!",
          description: "Your message has been sent. The team will be in touch soon.",
        });
        setShowEnquiryForm(false);
        setEnquiryData({ enquirer_name: '', enquirer_email: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending enquiry:', error);
      toast({
        title: "Error",
        description: "Failed to send enquiry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20 flex justify-center items-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-red-500 mb-4">Model Not Found</h1>
          <p className="text-gray-400 mb-8">The model you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/freelance-models"
            className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
          >
            <ArrowLeft size={18} /> Back to Models
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/freelance-models"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-8"
        >
          <ArrowLeft size={18} /> Back to Models
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image/Video Gallery */}
          <div>
            {/* Main Image or Video */}
            <div className="aspect-[3/4] rounded-lg overflow-hidden mb-4 border border-red-900/20 bg-black">
              {showVideo && model.video_url ? (
                <video 
                  src={`${BACKEND_URL}${model.video_url}`}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                  data-testid="model-video"
                />
              ) : (
                <img 
                  src={model.image_urls[selectedImage] ? `${BACKEND_URL}${model.image_urls[selectedImage]}` : 'https://via.placeholder.com/600x800?text=No+Image'}
                  alt={model.name}
                  className="w-full h-full object-cover"
                  data-testid="model-main-image"
                />
              )}
            </div>
            
            {/* Thumbnail Grid with Video */}
            <div className="grid grid-cols-5 gap-2">
              {/* Video Thumbnail */}
              {model.video_url && (
                <button
                  onClick={() => setShowVideo(true)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all relative bg-zinc-900 ${
                    showVideo 
                      ? 'border-red-500' 
                      : 'border-transparent hover:border-red-500/50'
                  }`}
                >
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Play size={24} className="text-white" fill="white" />
                  </div>
                  <span className="absolute bottom-1 left-1 text-xs text-white bg-black/70 px-1 rounded">Video</span>
                </button>
              )}
              
              {/* Image Thumbnails */}
              {model.image_urls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => { setSelectedImage(index); setShowVideo(false); }}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    !showVideo && selectedImage === index 
                      ? 'border-red-500' 
                      : 'border-transparent hover:border-red-500/50'
                  }`}
                >
                  <img 
                    src={`${BACKEND_URL}${url}`}
                    alt={`${model.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Model Info */}
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4" data-testid="model-name">
              {model.name}
            </h1>

            {/* Categories */}
            {model.categories && model.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {model.categories.map(cat => (
                  <span key={cat} className="bg-zinc-800 text-gray-300 px-3 py-1 rounded-full text-sm">
                    {cat}
                  </span>
                ))}
              </div>
            )}
            
            {model.bio && (
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {model.bio}
              </p>
            )}

            {/* Contact Links */}
            <div className="flex flex-wrap gap-4 mb-8">
              {model.instagram_url && (
                <a 
                  href={model.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-5 py-3 rounded-full transition-colors"
                  data-testid="model-instagram-link"
                >
                  <Instagram size={18} /> Instagram
                </a>
              )}
              <a 
                href={`mailto:${model.email}`}
                className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-5 py-3 rounded-full transition-colors"
                data-testid="model-email-link"
              >
                <Mail size={18} /> Email
              </a>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={() => setShowBookingModal(true)}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
                data-testid="book-model-btn"
              >
                <Calendar size={18} /> Book
              </button>

              <button
                onClick={() => setShowEnquiryForm(!showEnquiryForm)}
                className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
                data-testid="contact-model-btn"
              >
                <MessageSquare size={18} /> {showEnquiryForm ? 'Hide Form' : 'Contact'}
              </button>
              
              {/* Download PDF Button */}
              <a
                href={`${API}/models/${modelId}/portfolio-pdf`}
                download
                className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
                data-testid="download-pdf-btn"
              >
                <Download size={18} /> PDF
              </a>
            </div>

            {/* Social Share Buttons */}
            <div className="mb-8">
              <p className="text-gray-500 text-sm mb-3 flex items-center gap-2">
                <Share2 size={14} /> Share this profile:
              </p>
              <div className="flex gap-2">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors"
                  title="Share on Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Check out ${model.name}'s portfolio on EWS Productions!`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center text-white transition-colors"
                  title="Share on Twitter"
                >
                  <Twitter size={18} />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-blue-700 hover:bg-blue-800 flex items-center justify-center text-white transition-colors"
                  title="Share on LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast({ title: "Link Copied!", description: "Profile URL copied to clipboard." });
                  }}
                  className="w-10 h-10 rounded-full bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-white transition-colors"
                  title="Copy link"
                >
                  <LinkIcon size={18} />
                </button>
              </div>
            </div>

            {/* Enquiry Form */}
            {showEnquiryForm && (
              <form 
                onSubmit={handleEnquirySubmit}
                className="bg-zinc-900 rounded-lg p-6 border border-red-900/20"
                data-testid="model-enquiry-form"
              >
                <h3 className="text-xl font-semibold text-white mb-6">Send an Enquiry</h3>
                
                <div className="mb-4">
                  <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    <User size={14} /> Your Name *
                  </label>
                  <input
                    type="text"
                    name="enquirer_name"
                    value={enquiryData.enquirer_name}
                    onChange={handleEnquiryChange}
                    required
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                    data-testid="enquiry-name"
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    <Mail size={14} /> Your Email *
                  </label>
                  <input
                    type="email"
                    name="enquirer_email"
                    value={enquiryData.enquirer_email}
                    onChange={handleEnquiryChange}
                    required
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                    data-testid="enquiry-email"
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    <MessageSquare size={14} /> Message *
                  </label>
                  <textarea
                    name="message"
                    value={enquiryData.message}
                    onChange={handleEnquiryChange}
                    required
                    rows={4}
                    placeholder="Tell us about your project and how you'd like to work with this model..."
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
                    data-testid="enquiry-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
                  data-testid="enquiry-submit"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>Send Enquiry <Send size={16} /></>
                  )}
                </button>
              </form>
            )}

            {/* Stats */}
            <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20 mt-8">
              <h3 className="text-white font-semibold mb-4">Profile Stats</h3>
              <div className="text-gray-400 space-y-2">
                <p>Profile Views: <span className="text-white">{model.profile_views || 0}</span></p>
                <div className="flex items-center gap-2">
                  <span>Rating:</span>
                  <StarRating rating={Math.round(reviewStats.average)} size={16} />
                  <span className="text-white">{reviewStats.average > 0 ? reviewStats.average.toFixed(1) : 'No reviews yet'}</span>
                  {reviewStats.total > 0 && (
                    <span className="text-gray-500">({reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-semibold text-white flex items-center gap-3">
                <Star className="text-yellow-400" size={24} fill="currentColor" />
                Client Reviews
              </h2>
              {reviewStats.total > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <StarRating rating={Math.round(reviewStats.average)} size={18} />
                  <span className="text-white font-semibold">{reviewStats.average.toFixed(1)}</span>
                  <span className="text-gray-500">({reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''})</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-full font-semibold transition-colors"
              data-testid="write-review-btn"
            >
              <Star size={18} /> Write a Review
            </button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form 
              onSubmit={handleReviewSubmit}
              className="bg-zinc-900 rounded-lg p-6 border border-yellow-500/30 mb-8"
              data-testid="review-form"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Leave a Review</h3>
              
              {/* Star Rating Input */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-3 tracking-wider uppercase">
                  <Star size={14} /> Your Rating *
                </label>
                <StarRating 
                  rating={reviewData.rating} 
                  onRate={(rating) => setReviewData(prev => ({ ...prev, rating }))}
                  interactive={true}
                  size={32}
                />
              </div>
              
              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  <User size={14} /> Your Name *
                </label>
                <input
                  type="text"
                  name="reviewer_name"
                  value={reviewData.reviewer_name}
                  onChange={handleReviewChange}
                  required
                  className="w-full bg-black border border-yellow-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  data-testid="review-name"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  <Mail size={14} /> Your Email *
                </label>
                <input
                  type="email"
                  name="reviewer_email"
                  value={reviewData.reviewer_email}
                  onChange={handleReviewChange}
                  required
                  className="w-full bg-black border border-yellow-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  data-testid="review-email"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  <MessageSquare size={14} /> Your Review *
                </label>
                <textarea
                  name="comment"
                  value={reviewData.comment}
                  onChange={handleReviewChange}
                  required
                  rows={4}
                  placeholder="Share your experience working with this model..."
                  className="w-full bg-black border border-yellow-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                  data-testid="review-comment"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-700 disabled:cursor-not-allowed text-black py-3 rounded-full font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="review-submit"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>Submit Review <Star size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-red-900/20">
              <Star className="mx-auto text-gray-600 mb-4" size={40} />
              <p className="text-gray-400">No reviews yet. Be the first to leave a review!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div 
                  key={review.id}
                  className="bg-zinc-900 rounded-lg p-6 border border-red-900/20"
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-white font-semibold">{review.reviewer_name}</h4>
                      <p className="text-gray-500 text-sm">
                        {new Date(review.timestamp).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <StarRating rating={review.rating} size={16} />
                  </div>
                  <p className="text-gray-300">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BookingModal
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        talentType="model"
        talentId={modelId}
        talentName={model.name}
      />
    </div>
  );
};

export default ModelProfile;
